import React from 'react'
import TableCell from '@mui/material/TableCell'
import TableRow from '@mui/material/TableRow'
import Collapse from '@mui/material/Collapse'
import IconButton from '@mui/material/IconButton'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp'
import DeleteIcon from '@mui/icons-material/Delete'
import EditIcon from '@mui/icons-material/Edit'
import CheckIcon from '@mui/icons-material/Check'
import CloseIcon from '@mui/icons-material/Close'
import ImportExportIcon from '@mui/icons-material/ImportExport'
import CircularProgress from '@mui/material/CircularProgress'
import { useNavigate } from 'react-router-dom'
import {
  DraggableProvidedDraggableProps,
  DraggableProvidedDragHandleProps,
} from '@hello-pangea/dnd'
import { TColumn, IAction } from './tableTypes'

export interface IRow {
  columns: {
    normal: TColumn[]
    edit?: TColumn[]
  }
  dragProps?: DraggableProvidedDraggableProps
  dragHandleProps?: DraggableProvidedDragHandleProps
  dragRef?: (element?: HTMLElement) => void
  sortId?: number
  extraActions?: IAction[]
  showExpand?: boolean
  navigateTo?: string
  children?: React.ReactNode
  isLoading?: boolean
  disableButtons?: boolean
  onClick?: () => void
  onEditOpen?: () => void
  onEditSuccess?: () => void
  onDeleteOpen?: () => void
  onEditAbort?: () => void
  onAddSuccess?: () => void
  onAddAbort?: () => void
}

export const Row: React.FC<IRow> = ({
  columns,
  dragProps,
  dragHandleProps,
  dragRef,
  extraActions,
  showExpand,
  navigateTo,
  children,
  isLoading,
  disableButtons,
  onClick,
  onEditOpen,
  onEditSuccess,
  onEditAbort,
  onDeleteOpen,
  onAddSuccess,
  onAddAbort,
}) => {
  const [isChildTableOpen, setChildTableOpen] = React.useState<boolean>(false)
  const [isEditing, setIsEditing] = React.useState<boolean>(false)
  const [isHoveringButton, setIsHoveringButton] = React.useState<boolean>(false)
  const selectedColumns = isEditing ? columns.edit : columns.normal
  const navigate = useNavigate()
  let canHover = false
  if (!isHoveringButton && !isEditing && (!!onClick || !!navigateTo)) {
    canHover = true
  }

  return (
    <>
      <TableRow
        {...dragProps}
        ref={dragRef}
        hover={canHover}
        sx={{ '&:hover': { cursor: canHover ? 'pointer' : 'default' } }}
        onClick={() => {
          if (!isEditing && (navigateTo || onClick)) {
            navigateTo ? navigate(navigateTo) : onClick()
          }
        }}
      >
        {dragRef && (
          <TableCell
            size="small"
            sx={{
              color: '#707070',
              ':hover:': {
                cursor: 'grab',
              },
            }}
            {...dragHandleProps}
          >
            <ImportExportIcon />
          </TableCell>
        )}
        {showExpand && (
          <TableCell>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation()
                setChildTableOpen(!isChildTableOpen)
              }}
            >
              {isChildTableOpen ? (
                <KeyboardArrowUpIcon />
              ) : (
                <KeyboardArrowDownIcon />
              )}
            </IconButton>
          </TableCell>
        )}
        {selectedColumns?.map((column, index) => (
          <TableCell align="left" key={index}>
            {column}
          </TableCell>
        ))}
        {onAddSuccess && onAddAbort && (
          <TableCell align="right" sx={{ minWidth: '100px' }}>
            {extraActions &&
              extraActions.map((action, index) => (
                <IconButton
                  key={index}
                  size="small"
                  disabled={true}
                  onMouseEnter={() => setIsHoveringButton(true)}
                  onMouseLeave={() => setIsHoveringButton(false)}
                >
                  {action.icon}
                </IconButton>
              ))}
            <IconButton
              color="success"
              size="small"
              disabled={isLoading}
              onClick={() => onAddSuccess()}
            >
              {!isLoading ? (
                <CheckIcon />
              ) : (
                <CircularProgress color="inherit" size={20} />
              )}
            </IconButton>
            <IconButton
              color="secondary"
              size="small"
              disabled={isLoading}
              onClick={() => onAddAbort()}
            >
              <CloseIcon />
            </IconButton>
          </TableCell>
        )}
        {isEditing ? (
          <TableCell align="right" sx={{ minWidth: '100px' }}>
            {extraActions &&
              extraActions.map((action, index) => (
                <IconButton key={index} size="small" disabled={true}>
                  {action.icon}
                </IconButton>
              ))}
            <IconButton
              color="success"
              size="small"
              onClick={() => {
                setIsEditing(false)
                onEditSuccess && onEditSuccess()
              }}
            >
              <CheckIcon />
            </IconButton>
            <IconButton
              color="secondary"
              size="small"
              onClick={() => {
                setIsEditing(false)
                onEditAbort && onEditAbort()
              }}
            >
              <CloseIcon />
            </IconButton>
          </TableCell>
        ) : (
          <>
            {onEditOpen && onDeleteOpen ? (
              <TableCell align="right" sx={{ minWidth: '100px' }}>
                {extraActions &&
                  extraActions.map((action, index) => (
                    <IconButton
                      key={index}
                      size="small"
                      disabled={action.disabled}
                      onClick={(e) => {
                        e.stopPropagation()
                        action.onClick && action.onClick()
                      }}
                      onMouseEnter={() => setIsHoveringButton(true)}
                      onMouseLeave={() => setIsHoveringButton(false)}
                    >
                      {action.icon}
                    </IconButton>
                  ))}
                <IconButton
                  size="small"
                  disabled={disableButtons}
                  onClick={(e) => {
                    e.stopPropagation()
                    setIsEditing(true)
                    onEditOpen && onEditOpen()
                  }}
                  onMouseEnter={() => setIsHoveringButton(true)}
                  onMouseLeave={() => setIsHoveringButton(false)}
                >
                  <EditIcon />
                </IconButton>
                <IconButton
                  size="small"
                  disabled={disableButtons}
                  onClick={(e) => {
                    e.stopPropagation()
                    onDeleteOpen && onDeleteOpen()
                  }}
                  onMouseEnter={() => setIsHoveringButton(true)}
                  onMouseLeave={() => setIsHoveringButton(false)}
                >
                  <DeleteIcon />
                </IconButton>
              </TableCell>
            ) : (
              onDeleteOpen && (
                <TableCell align="right">
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation()
                      onDeleteOpen && onDeleteOpen()
                    }}
                  >
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              )
            )}
          </>
        )}
      </TableRow>
      {children && (
        <TableRow>
          <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={6}>
            <Collapse in={isChildTableOpen} timeout="auto" unmountOnExit>
              {children}
            </Collapse>
          </TableCell>
        </TableRow>
      )}
    </>
  )
}

export default Row
